import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ServiceBillingService } from '../../service-billing/service-billing.service';

@Component({
  selector: 'app-service-billing-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule],
  templateUrl: './service-billing-list.component.html',
  styleUrls: ['./service-billing-list.component.scss'],
})
export class ServiceBillingListComponent implements OnInit, OnDestroy {
  serviceBillingList: any[] = [];
  serviceTotalCount: number = 0;
  servicePage: number = 1;
  pageSize: number = 10;
  isLoading: boolean = false;
  searchControl = new FormControl('');
  private destroy$ = new Subject<void>();

  // Service Summary
  serviceTotalBills = 0;
  serviceTotalAmount = 0;
  serviceTotalGst = 0;
  serviceTotalDiscount = 0;

  // Report Modal
  showReportModal = false;
  selectedPreset: string | null = null;
  fromDate: string | null = null;
  toDate: string | null = null;

  constructor(
    private serviceBillingService: ServiceBillingService,
    private toastr: ToastrService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.getServiceBillingList();
    this.searchControl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.getServiceBillingList(1);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getServiceBillingList(page: number = 1, limit: number = 10) {
    this.servicePage = page;
    this.isLoading = true;
    const searchTerm = this.searchControl.value || '';
    this.serviceBillingService
      .getServiceInvoices(page, limit, searchTerm)
      .subscribe({
        next: (res: any) => {
          this.serviceBillingList = res.data || [];
          this.serviceTotalCount = res.total || 0;
          this.calculateServiceSummary();
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error fetching service billing list', err);
          this.toastr.error('Failed to load service bills');
          this.isLoading = false;
        },
      });
  }

  calculateServiceSummary() {
    this.serviceTotalBills = this.serviceBillingList.length;
    this.serviceTotalAmount = this.serviceBillingList.reduce(
      (sum, b) => sum + (parseFloat(b.grandTotal || b.total) || 0),
      0,
    );
    this.serviceTotalGst = this.serviceBillingList.reduce(
      (sum, b) => sum + (parseFloat(b.gstAmount) || 0),
      0,
    );
    this.serviceTotalDiscount = this.serviceBillingList.reduce(
      (sum, b) => sum + (parseFloat(b.totalDiscount || b.discount) || 0),
      0,
    );
  }

  get serviceTotalPages(): number {
    return Math.ceil(this.serviceTotalCount / this.pageSize);
  }

  get serviceVisiblePages(): number[] {
    const maxPagesToShow = 5;
    const total = this.serviceTotalPages;
    if (total <= maxPagesToShow)
      return Array.from({ length: total }, (_, i) => i + 1);

    let start = Math.max(this.servicePage - Math.floor(maxPagesToShow / 2), 1);
    let end = start + maxPagesToShow - 1;
    if (end > total) {
      end = total;
      start = end - maxPagesToShow + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  downloadServicePDF(id: string) {
    this.serviceBillingService
      .generateInvoice(id, {})
      .subscribe((blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `service-invoice-${id}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  formatCurrency(value: string | number): string {
    const num =
      typeof value === 'string' ? parseFloat(value || '0') : value || 0;
    return `₹${num.toFixed(2)}`;
  }

  formatDateTime(dateStr: string): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return isNaN(date.getTime())
      ? 'Invalid Date'
      : date.toLocaleString('en-IN');
  }

  getProviderNames(bill: any): string {
    if (!bill.items || !Array.isArray(bill.items) || bill.items.length === 0) {
      return 'N/A';
    }

    const providerNames = bill.items
      .map((item: any) => item.provider?.name)
      .filter((name: string) => name)
      .filter(
        (name: string, index: number, self: string[]) =>
          self.indexOf(name) === index,
      ); // Remove duplicates

    return providerNames.length > 0 ? providerNames.join(', ') : 'N/A';
  }

  refreshData() {
    this.getServiceBillingList(this.servicePage);
  }

  // Report Modal Methods
  openReportModal() {
    this.showReportModal = true;
  }

  closeReportModal() {
    this.showReportModal = false;
  }

  setRelative(preset: string) {
    this.selectedPreset = preset;
    this.fromDate = null;
    this.toDate = null;
  }

  downloadReport() {
    const payload: any = {};
    if (this.selectedPreset) payload.preset = this.selectedPreset;
    if (this.fromDate) payload.fromDate = this.fromDate;
    if (this.toDate) payload.toDate = this.toDate;

    if (!payload.preset && !payload.fromDate && !payload.toDate) {
      this.toastr.warning('Please select a preset or date range');
      return;
    }

    this.serviceBillingService.exportServiceInvoices(payload).subscribe({
      next: (blob: Blob) => {
        let filename = 'service-billing-report.xlsx';
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success('Report downloaded successfully');
        this.closeReportModal();
      },
      error: (err) => {
        console.error('Error downloading report', err);
        this.toastr.error('Failed to download report');
      },
    });
  }

  navigateToDetails(id: string) {
    this.router.navigate(['/service-billing-details', id]);
  }
}
